import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flower, CheckCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FlowerExchange() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    agreed: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, exchangeRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/flower-exchange/my-registration`, { withCredentials: true })
        ]);
        setUser(userRes.data);
        setExistingRegistration(exchangeRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreed) {
      toast.error('You must agree to mail a floral arrangement');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/flower-exchange/register`, formData, { withCredentials: true });
      toast.success('Registration successful! Check your email for next steps.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-xl font-heading">Loading...</div>
      </div>
    );
  }

  if (existingRegistration) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4">
          <Button 
            data-testid="back-to-dashboard-button"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            size="sm"
          >
            ← Back to Dashboard
          </Button>
        </nav>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl p-8 md:p-12 text-center"
          >
            <CheckCircle size={64} weight="fill" className="text-accent mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">You're All Set!</h1>
            <p className="text-muted-foreground mb-8">
              You're registered for the 2026 Flower Exchange. We'll email you with your exchange partner details soon.
            </p>
            <div className="bg-muted rounded-2xl p-6 text-left mb-8">
              <h3 className="font-heading font-semibold mb-4">Your Registration Details:</h3>
              <div className="space-y-2 text-sm">
                <p><strong>Name:</strong> {existingRegistration.full_name}</p>
                <p><strong>Address:</strong> {existingRegistration.address_line1}</p>
                {existingRegistration.address_line2 && <p>{existingRegistration.address_line2}</p>}
                <p>{existingRegistration.city}, {existingRegistration.state} {existingRegistration.zip_code}</p>
                <p><strong>Phone:</strong> {existingRegistration.phone}</p>
              </div>
            </div>
            <Button 
              data-testid="return-dashboard-button"
              onClick={() => navigate('/dashboard')}
              className="rounded-full bg-primary hover:bg-primary-hover"
            >
              Return to Dashboard
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-white/70 backdrop-blur-lg border-b border-white/20 px-6 py-4">
        <Button 
          data-testid="back-to-dashboard-button"
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          size="sm"
        >
          ← Back to Dashboard
        </Button>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <Flower size={64} weight="fill" className="text-secondary mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Flower Exchange 2026</h1>
            <p className="text-muted-foreground text-lg">
              Join our yearly tradition! Register to exchange beautiful floral arrangements with a fellow member.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl p-8 md:p-12 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="full_name" data-testid="full-name-label">Full Name *</Label>
              <Input
                id="full_name"
                data-testid="full-name-input"
                type="text"
                required
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line1" data-testid="address-line1-label">Address Line 1 *</Label>
              <Input
                id="address_line1"
                data-testid="address-line1-input"
                type="text"
                required
                value={formData.address_line1}
                onChange={(e) => setFormData({...formData, address_line1: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address_line2" data-testid="address-line2-label">Address Line 2</Label>
              <Input
                id="address_line2"
                data-testid="address-line2-input"
                type="text"
                value={formData.address_line2}
                onChange={(e) => setFormData({...formData, address_line2: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city" data-testid="city-label">City *</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state" data-testid="state-label">State *</Label>
                <Input
                  id="state"
                  data-testid="state-input"
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => setFormData({...formData, state: e.target.value})}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code" data-testid="zip-code-label">Zip Code *</Label>
                <Input
                  id="zip_code"
                  data-testid="zip-code-input"
                  type="text"
                  required
                  value={formData.zip_code}
                  onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" data-testid="phone-label">Phone Number *</Label>
              <Input
                id="phone"
                data-testid="phone-input"
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="rounded-xl"
              />
            </div>

            <div className="bg-secondary/10 rounded-2xl p-6 space-y-4">
              <h3 className="font-heading font-semibold text-lg">Agreement</h3>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="agreed"
                  data-testid="agreement-checkbox"
                  checked={formData.agreed}
                  onCheckedChange={(checked) => setFormData({...formData, agreed: checked})}
                />
                <Label htmlFor="agreed" className="text-sm leading-relaxed cursor-pointer">
                  I agree to mail a beautiful floral arrangement to my assigned exchange partner by the specified date. 
                  I understand this is a binding commitment to participate in spreading joy through flowers.
                </Label>
              </div>
            </div>

            <Button
              data-testid="submit-registration-button"
              type="submit"
              disabled={submitting || !formData.agreed}
              className="w-full rounded-full bg-primary hover:bg-primary-hover font-bold py-6 text-lg"
            >
              {submitting ? 'Registering...' : 'Complete Registration'}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}